/**
 * "An account has been used in this browser" — a hint, not a fact.
 *
 * Signup asks the user to confirm before creating a SECOND wallet on a device that
 * already has one, because someone who taps "create a wallet" with an account
 * already here has usually come to sign in. To ask that question the signed-out
 * signup screen has to know an account exists, and it has no session to ask.
 *
 * WHY A STORED FLAG AND NOT A LOOKUP. WebAuthn deliberately refuses to tell a
 * relying party which credentials a device holds — that is credential enumeration,
 * and it is withheld by design. A silent `get()` probe is inconsistent across
 * browsers and can surface UI. So there is no way to detect an existing account;
 * the only thing available is remembering that one was used here.
 *
 * DELIBERATELY BEST EFFORT. It does not survive a private window, cleared storage,
 * or another browser — and passkeys SYNC while localStorage does not, so a user who
 * signed up on a Mac gets no prompt on their iPhone. That is acceptable because a
 * missed prompt costs nothing: signup still works, and the second account is
 * genuinely valid. This must never gate anything, only ask.
 *
 * Kept in its own module, mirroring createPasskeyPref, so the writers (login and
 * signup) and the reader (the signup screen) share one seam.
 */

/**
 * localStorage key recording that an account has been signed in or created in this
 * browser. Versioned like the store's other persisted keys so the meaning can change
 * later without an old value being misread.
 */
export const ACCOUNT_ON_DEVICE_KEY = 'pmw.accountOnDevice.v1';

/**
 * Whether an account has been used in this browser before.
 *
 * A blocked or unavailable localStorage reads as `false`, so the gate fails toward
 * NOT asking. That is the right direction: an unnecessary confirmation on a fresh
 * device is a worse experience than a missing one, and the prompt is a courtesy
 * rather than a safety control.
 *
 * @returns True when signup should confirm before creating another account.
 */
export function hasAccountOnDevice(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(ACCOUNT_ON_DEVICE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Record that an account was signed in or created here. Called from both the login
 * and signup success paths — either proves an account exists on this device.
 */
export function rememberAccountOnDevice(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(ACCOUNT_ON_DEVICE_KEY, 'true');
    }
  } catch {
    // Best effort: a storage failure only means the confirmation is skipped on a
    // later signup, which costs nothing.
  }
}
